import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Save } from "lucide-react";
import FileUploader from "./FileUploader";

const empty = { slug: "", title: "", category: "", cover_image: "", excerpt: "", body: "", published: true };

export default function AdminMedia() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(null);

    const load = useCallback(
        () => api.get("/blog?all=true").then((r) => setItems(r.data || [])),
        []
    );
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        try {
            if (form.id) await api.put(`/blog/${form.id}`, form);
            else await api.post("/blog", form);
            toast.success("Saved");
            setForm(null);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete post?")) return;
        await api.delete(`/blog/${id}`);
        toast.success("Deleted");
        load();
    };

    return (
        <div data-testid="admin-media">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-semibold">Media / News</h1>
                    <p className="text-sm text-slate-500 mt-1">Blog-style updates and news stories.</p>
                </div>
                <button onClick={() => setForm({ ...empty })} data-testid="post-add-btn" className="px-4 py-2 rounded-md bg-brand-forest text-white text-sm font-semibold inline-flex items-center gap-2"><Plus size={14} /> New post</button>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-5 py-3">Title</th>
                            <th className="text-left px-5 py-3">Category</th>
                            <th className="text-left px-5 py-3">Slug</th>
                            <th className="text-left px-5 py-3">Status</th>
                            <th className="text-right px-5 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((b) => (
                            <tr key={b.id} className="border-t border-slate-100">
                                <td className="px-5 py-3">{b.title}</td>
                                <td className="px-5 py-3 text-slate-500">{b.category}</td>
                                <td className="px-5 py-3 text-slate-500">{b.slug}</td>
                                <td className="px-5 py-3">
                                    <span className={`text-xs font-semibold px-2 py-1 rounded ${b.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{b.published ? "Published" : "Draft"}</span>
                                </td>
                                <td className="px-5 py-3 text-right">
                                    <button onClick={() => setForm({ ...b })} className="p-2 hover:bg-slate-100 rounded"><Pencil size={14} /></button>
                                    <button onClick={() => remove(b.id)} className="p-2 hover:bg-red-50 text-red-600 rounded"><Trash2 size={14} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {form && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setForm(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
                            <h2 className="font-semibold">{form.id ? "Edit post" : "New post"}</h2>
                            <button onClick={() => setForm(null)}><X size={18} /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm" />
                            <div className="grid grid-cols-2 gap-3">
                                <input placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="px-3 py-2 rounded border border-slate-200 text-sm" />
                                <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-3 py-2 rounded border border-slate-200 text-sm" />
                            </div>
                            <textarea rows="2" placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm resize-none" />
                            <textarea rows="10" placeholder="Body (plain text)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm resize-none font-mono" />
                            <FileUploader kind="image" current={form.cover_image} label="Cover image" onUploaded={(r) => setForm({ ...form, cover_image: r.url })} />
                            <input placeholder="Or paste image URL" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className="w-full px-3 py-2 rounded border border-slate-200 text-sm" />
                            <label className="inline-flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Published
                            </label>
                        </div>
                        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
                            <button onClick={() => setForm(null)} className="px-4 py-2 rounded text-sm">Cancel</button>
                            <button onClick={save} className="px-4 py-2 rounded bg-brand-forest text-white text-sm font-semibold inline-flex items-center gap-2"><Save size={14} /> Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
